/**
 * 工具调用服务
 * 提供 Agent 调用工具的前端接口
 */

import { api } from './api';

// ==================== 类型定义 ====================

export interface ToolSchema {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, {
      type: string;
      description: string;
      default?: any;
    }>;
    required: string[];
  };
}

export interface ToolExecuteRequest {
  tool_name: string;
  parameters?: Record<string, any>;
  context?: Record<string, any>;
}

export interface ToolExecuteResult {
  success: boolean;
  tool_name: string;
  result?: {
    log_id: string;
    msg: string;
    code: number;
    data: any;
  };
  error?: string;
}

export interface ToolExecutionHistory {
  tool_name: string;
  parameters: Record<string, any>;
  result: any;
  timestamp: string;
}

export interface SearchResult {
  title: string;
  url: string;
  image_url: string;
  logo_url: string;
  sitename: string;
  summary: string;
  has_image: boolean;
}

// ==================== 工具 API ====================

/**
 * 获取所有工具列表
 */
export async function listTools(): Promise<{
  success: boolean;
  data: ToolSchema[];
  total: number;
  degraded?: boolean;
  message?: string;
}> {
  try {
    const response = await api.get('/juben/tools/list');
    return response;
  } catch {
    return { success: true, data: [], total: 0, degraded: true, message: '工具列表服务暂不可用' };
  }
}

/**
 * 获取工具 Schema（用于 LLM Function Calling）
 */
export async function getToolSchemas(): Promise<{
  success: boolean;
  data: ToolSchema[];
  format: string;
  degraded?: boolean;
  message?: string;
}> {
  try {
    const response = await api.get('/juben/tools/schemas');
    return response;
  } catch {
    return { success: true, data: [], format: 'openai', degraded: true, message: '工具 Schema 服务暂不可用' };
  }
}

/**
 * 执行单个工具
 */
export async function executeTool(request: ToolExecuteRequest): Promise<ToolExecuteResult> {
  try {
    const response = await api.post<ToolExecuteResult>('/juben/tools/execute', request);
    return response;
  } catch {
    return {
      success: false,
      tool_name: request.tool_name,
      error: '工具执行服务暂不可用',
    };
  }
}

/**
 * 批量执行工具
 */
export async function batchExecuteTools(tools: ToolExecuteRequest[]): Promise<{
  success: boolean;
  data: ToolExecuteResult[];
  total: number;
  degraded?: boolean;
  message?: string;
}> {
  try {
    const response = await api.post('/juben/tools/batch_execute', { tools });
    return response;
  } catch {
    return {
      success: true,
      data: tools.map((tool) => ({
        success: false,
        tool_name: tool.tool_name,
        error: '批量工具执行服务暂不可用',
      })),
      total: tools.length,
      degraded: true,
      message: '批量工具执行服务暂不可用',
    };
  }
}

/**
 * 获取工具执行历史
 */
export async function getToolHistory(limit: number = 50): Promise<{
  success: boolean;
  data: ToolExecutionHistory[];
  total: number;
  degraded?: boolean;
  message?: string;
}> {
  try {
    const response = await api.get(`/juben/tools/history?limit=${limit}`);
    return response;
  } catch {
    return { success: true, data: [], total: 0, degraded: true, message: '工具历史服务暂不可用' };
  }
}

/**
 * 获取单个工具的 Schema
 */
export async function getToolSchema(toolName: string): Promise<{
  success: boolean;
  data: ToolSchema | null;
  degraded?: boolean;
  message?: string;
}> {
  try {
    const response = await api.get(`/juben/tools/${toolName}/schema`);
    return response;
  } catch {
    return { success: true, data: null, degraded: true, message: '工具 Schema 服务暂不可用' };
  }
}

// ==================== 便捷接口 ====================

/**
 * 快速搜索
 */
export async function quickSearch(query: string, maxResults: number = 10): Promise<{
  success: boolean;
  query: string;
  results: SearchResult[];
  total: number;
  degraded?: boolean;
  message?: string;
}> {
  try {
    const params = new URLSearchParams({
      query,
      max_results: String(maxResults),
    });
    const response = await api.post(`/juben/tools/search?${params.toString()}`);
    return response;
  } catch {
    return { success: true, query, results: [], total: 0, degraded: true, message: '快速搜索服务暂不可用' };
  }
}

/**
 * 快速百科查询
 */
export async function quickBaike(query: string, includeVideos: boolean = false): Promise<{
  success: boolean;
  query: string;
  baike: any;
  videos: any[];
  degraded?: boolean;
  message?: string;
}> {
  try {
    const params = new URLSearchParams({
      query,
      include_videos: String(includeVideos),
    });
    const response = await api.post(`/juben/tools/baike?${params.toString()}`);
    return response;
  } catch {
    return { success: true, query, baike: null, videos: [], degraded: true, message: '快速百科服务暂不可用' };
  }
}

/**
 * 工具系统健康检查
 */
export async function healthCheck(): Promise<{
  status: string;
  tools_registered: number;
  tools: string[];
  degraded?: boolean;
  message?: string;
}> {
  try {
    const response = await api.get('/juben/tools/health');
    return response;
  } catch {
    return { status: 'degraded', tools_registered: 0, tools: [], degraded: true, message: '工具健康检查暂不可用' };
  }
}

// ==================== 导出 ====================

const toolService = {
  // 工具管理
  listTools,
  getToolSchemas,
  getToolSchema,

  // 工具执行
  executeTool,
  batchExecuteTools,

  // 历史记录
  getToolHistory,

  // 便捷接口
  quickSearch,
  quickBaike,
  healthCheck
};

export default toolService;
